import {
  ChangeEvent,
  ComponentType,
  SVGProps,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { RangeOperator } from '@sib/types';
import {
  RangeOperatorBetween,
  RangeOperatorNotBetween,
  RangeOperatorEqualTo,
  RangeOperatorLessThan,
  RangeOperatorLessThanOrEqual,
  RangeOperatorGreaterThan,
  RangeOperatorGreaterThanOrEqual,
} from '@sib/ui/icons';

export const DEFAULT_TO_VALUE = 1_000_000;

export const NUMBER_RANGE_UPPER_MAX = 100_000_000;

export const ALL_RANGE_OPERATORS: RangeOperator[] = [
  'between',
  // TODO: Add back the operators when backend supports them.
  // 'nbetween',
  // 'eq',
  // 'lt',
  // 'lte',
  // 'gt',
  // 'gte',
] as const;

const DEFAULT_RANGE_OPERATOR = 'between';

export const RANGE_OPERATOR_ICONS: Record<
  RangeOperator,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  between: RangeOperatorBetween,
  nbetween: RangeOperatorNotBetween,
  eq: RangeOperatorEqualTo,
  lt: RangeOperatorLessThan,
  lte: RangeOperatorLessThanOrEqual,
  gt: RangeOperatorGreaterThan,
  gte: RangeOperatorGreaterThanOrEqual,
};

const RANGE_OPERATOR_TRIGGER_SYMBOLS: Record<RangeOperator, string> = {
  between: '••',
  nbetween: '••',
  eq: '=',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

export const isBinaryOperator = (op: RangeOperator) =>
  op === DEFAULT_RANGE_OPERATOR || op === 'nbetween';

interface UseNumberRangeFilterOptions {
  defaultFrom?: number;
  defaultTo?: number;
  unit?: string;
  placeholder?: string;
  operators?: RangeOperator[];
  onChange?: (operator: RangeOperator, from: string, to: string) => void;
  onClear?: () => void;
}

const parseOperator = (raw: string | undefined): RangeOperator =>
  ALL_RANGE_OPERATORS.includes(raw as RangeOperator)
    ? (raw as RangeOperator)
    : DEFAULT_RANGE_OPERATOR;

export function useNumberRangeFilter(
  value: string[],
  min: number,
  max: number,
  options: UseNumberRangeFilterOptions = {}
) {
  const [open, setOpen] = useState(false);

  const {
    defaultFrom = min,
    defaultTo = max,
    unit,
    placeholder,
    operators = ALL_RANGE_OPERATORS,
    onChange,
    onClear,
  } = options;

  const isEmptyFilter = value.length === 1 && value[0] === EMPTY_FILTER_VALUE;

  const appliedOperator = isEmptyFilter
    ? DEFAULT_RANGE_OPERATOR
    : parseOperator(value[0]);
  const appliedFrom =
    isEmptyFilter || !value[1] ? defaultFrom : Number(value[1]);
  const appliedTo = isEmptyFilter || !value[2] ? defaultTo : Number(value[2]);

  const [operator, setOperator] = useState<RangeOperator>(appliedOperator);
  const [from, setFrom] = useState<number | ''>(appliedFrom);
  const [to, setTo] = useState<number | ''>(appliedTo);

  const clamp = useCallback(
    (n: number) => Math.min(max, Math.max(min, n)),
    [min, max]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const nextOperator = isEmptyFilter
          ? DEFAULT_RANGE_OPERATOR
          : parseOperator(value[0]);
        const nextFrom = value[1] ? Number(value[1]) : defaultFrom;
        const nextTo = value[2] ? Number(value[2]) : defaultTo;
        setOperator(nextOperator);
        setFrom(clamp(nextFrom));
        setTo(clamp(nextTo));
      }

      setOpen(nextOpen);
    },
    [clamp, defaultFrom, defaultTo, value, isEmptyFilter]
  );

  const handleOperatorChange = useCallback(
    (nextOperator: RangeOperator) => {
      setOperator(nextOperator);
      if (!isBinaryOperator(nextOperator)) setTo(max);
    },
    [max]
  );

  const handleFromInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setFrom('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setFrom(v);
      if (isBinaryOperator(operator) && to !== '' && v > to) setTo(v);
    },
    [clamp, to, operator]
  );

  const handleToInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setTo('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setTo(v);
      if (from !== '' && v < from) setFrom(v);
    },
    [clamp, from]
  );

  const handleClear = useCallback(() => {
    setOperator(DEFAULT_RANGE_OPERATOR);
    setFrom(defaultFrom);
    setTo(defaultTo);
    onClear?.();
  }, [defaultFrom, defaultTo, onClear]);

  const formatWithUnit = useCallback(
    (v: number) => (unit ? `${v} ${unit}` : String(v)),
    [unit]
  );

  const handleConfirm = useCallback(() => {
    const fromNum = from === '' ? defaultFrom : clamp(from);
    const toNum = to === '' ? defaultTo : clamp(to);
    onChange?.(operator, String(fromNum), String(toNum));
    handleOpenChange(false);
  }, [
    from,
    to,
    defaultFrom,
    defaultTo,
    clamp,
    operator,
    onChange,
    handleOpenChange,
  ]);

  const hasAppliedRange =
    value.length >= 2 &&
    value[0] !== EMPTY_FILTER_VALUE &&
    value[1] !== undefined;

  const isDefaultRange = isBinaryOperator(operator)
    ? (from === '' || from === defaultFrom) && (to === '' || to === defaultTo)
    : from === '' || from === defaultFrom;

  const buttonLabel = useMemo(() => {
    if (!hasAppliedRange) return placeholder ?? null;

    const symbol = RANGE_OPERATOR_TRIGGER_SYMBOLS[appliedOperator];

    if (isBinaryOperator(appliedOperator))
      return `${symbol} ${formatWithUnit(appliedFrom)} - ${formatWithUnit(appliedTo)}`;

    return `${symbol} ${formatWithUnit(appliedFrom)}`;
  }, [
    hasAppliedRange,
    appliedOperator,
    appliedFrom,
    appliedTo,
    placeholder,
    formatWithUnit,
  ]);

  return {
    operator,
    operators,
    from,
    to,
    appliedOperator,
    appliedFrom,
    appliedTo,
    hasAppliedRange,
    isDefaultRange,
    buttonLabel,
    open,
    handleOperatorChange,
    handleFromInputChange,
    handleToInputChange,
    handleClear,
    handleConfirm,
    formatWithUnit,
    setOpen: handleOpenChange,
  };
}
